import { useQuery } from '@tanstack/react-query';
import { api, type SessionInfo } from '../lib/api.js';
import { PageHeader, SectionLabel, Card, StatusPill, Sparkline, BarTrack } from '../components/ui.js';

interface SiteHealthProps {
  session: SessionInfo;
}

interface LatestScores {
  performance: number | null;
  accessibility: number | null;
  best_practices: number | null;
  seo: number | null;
  lcp_ms: number | null;
  fcp_ms: number | null;
  tbt_ms: number | null;
  cls: number | null;
}

interface HistoryPoint {
  run_at: string;
  performance: number | null;
  seo: number | null;
}

interface Finding {
  id: string;
  rule: string;
  severity: string;
  message: string;
}

interface SiteHealthResponse {
  latest_scores: LatestScores | null;
  history: HistoryPoint[];
  findings: Finding[];
  last_run_at: string | null;
}

function lcpStatus(ms: number | null): 'ok' | 'warn' | 'bad' {
  if (ms == null) return 'neutral' as 'ok';
  if (ms <= 2500) return 'ok';
  if (ms <= 4000) return 'warn';
  return 'bad';
}

function clsStatus(cls: number | null): 'ok' | 'warn' | 'bad' {
  if (cls == null) return 'neutral' as 'ok';
  if (cls <= 0.1) return 'ok';
  if (cls <= 0.25) return 'warn';
  return 'bad';
}

function fcpStatus(ms: number | null): 'ok' | 'warn' | 'bad' {
  if (ms == null) return 'neutral' as 'ok';
  if (ms <= 1800) return 'ok';
  if (ms <= 3000) return 'warn';
  return 'bad';
}

function tbtStatus(ms: number | null): 'ok' | 'warn' | 'bad' {
  if (ms == null) return 'neutral' as 'ok';
  if (ms <= 200) return 'ok';
  if (ms <= 600) return 'warn';
  return 'bad';
}

function scoreColor(score: number | null): string {
  if (score == null) return 'text-cream/40';
  if (score >= 90) return 'text-emerald-300/90';
  if (score >= 50) return 'text-amber-300/90';
  return 'text-rose-300/90';
}

export function SiteHealthPage({ session }: SiteHealthProps) {
  const accountId = session.accounts[0]?.account_id ?? '';

  const { data, isLoading, isError } = useQuery<SiteHealthResponse>({
    queryKey: ['site-health', accountId],
    queryFn: () => api.get<SiteHealthResponse>(`/api/dashboard/client/site-health?account_id=${accountId}`),
    staleTime: 5 * 60_000,
    enabled: !!accountId,
  });

  const scores = data?.latest_scores ?? null;
  const history = data?.history ?? [];
  const findings = data?.findings ?? [];
  const lastRunAt = data?.last_run_at ?? null;

  // Build sparkline arrays from history (chronological, most recent last).
  const perfTrend = [...history].reverse().map((h) => h.performance ?? 0);
  const seoTrend = [...history].reverse().map((h) => h.seo ?? 0);

  const criticals = findings.filter((f) => f.severity === 'critical');
  const warnings = findings.filter((f) => f.severity !== 'critical');

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Site Health"
        title={<>Lighthouse scores, CWV, <em className="text-cream/80">findings.</em></>}
        subtitle="Bloom runs Lighthouse automatically and surfaces any regressions here."
      />

      {isLoading && (
        <div className="text-sm text-cream/40">Loading site health data…</div>
      )}

      {isError && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          Couldn't load site health data right now. Please refresh in a moment.
        </div>
      )}

      {!isLoading && !isError && !scores && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          No data yet — Bloom is collecting data for this account.
        </div>
      )}

      {scores && (
        <>
          {lastRunAt && (
            <div className="text-[11px] text-cream/35 -mb-8">
              Last run: {new Date(lastRunAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}

          <section>
            <SectionLabel>Lighthouse scores</SectionLabel>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Performance</div>
                <div className={`font-serif text-4xl tracking-tightest mt-1 tabular-nums ${scoreColor(scores.performance)}`}>
                  {scores.performance ?? '—'}
                </div>
                {perfTrend.length > 1 && (
                  <div className="mt-3"><Sparkline values={perfTrend} height={40} color="rgba(110, 231, 183, 0.7)" /></div>
                )}
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Accessibility</div>
                <div className={`font-serif text-4xl tracking-tightest mt-1 tabular-nums ${scoreColor(scores.accessibility)}`}>
                  {scores.accessibility ?? '—'}
                </div>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Best practices</div>
                <div className={`font-serif text-4xl tracking-tightest mt-1 tabular-nums ${scoreColor(scores.best_practices)}`}>
                  {scores.best_practices ?? '—'}
                </div>
              </Card>
              <Card>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">SEO</div>
                <div className={`font-serif text-4xl tracking-tightest mt-1 tabular-nums ${scoreColor(scores.seo)}`}>
                  {scores.seo ?? '—'}
                </div>
                {seoTrend.length > 1 && (
                  <div className="mt-3"><Sparkline values={seoTrend} height={40} color="rgba(244, 114, 182, 0.7)" /></div>
                )}
              </Card>
            </div>
          </section>

          <section>
            <SectionLabel>Core Web Vitals</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-cream/40">LCP</span>
                  {scores.lcp_ms != null && (
                    <StatusPill tone={lcpStatus(scores.lcp_ms)}>
                      {lcpStatus(scores.lcp_ms) === 'ok' ? 'good' : lcpStatus(scores.lcp_ms) === 'warn' ? 'needs work' : 'poor'}
                    </StatusPill>
                  )}
                </div>
                <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">
                  {scores.lcp_ms != null ? `${(scores.lcp_ms / 1000).toFixed(1)}s` : '—'}
                </div>
                <div className="text-[11px] text-cream/45 mt-1">target: &lt;2.5s</div>
                {scores.lcp_ms != null && (
                  <div className="mt-3">
                    <BarTrack value={scores.lcp_ms} max={6000} tone={lcpStatus(scores.lcp_ms)} />
                  </div>
                )}
              </Card>
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-cream/40">FCP</span>
                  {scores.fcp_ms != null && (
                    <StatusPill tone={fcpStatus(scores.fcp_ms)}>
                      {fcpStatus(scores.fcp_ms) === 'ok' ? 'good' : fcpStatus(scores.fcp_ms) === 'warn' ? 'needs work' : 'poor'}
                    </StatusPill>
                  )}
                </div>
                <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">
                  {scores.fcp_ms != null ? `${(scores.fcp_ms / 1000).toFixed(1)}s` : '—'}
                </div>
                <div className="text-[11px] text-cream/45 mt-1">target: &lt;1.8s</div>
                {scores.fcp_ms != null && (
                  <div className="mt-3">
                    <BarTrack value={scores.fcp_ms} max={4000} tone={fcpStatus(scores.fcp_ms)} />
                  </div>
                )}
              </Card>
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-cream/40">TBT</span>
                  {scores.tbt_ms != null && (
                    <StatusPill tone={tbtStatus(scores.tbt_ms)}>
                      {tbtStatus(scores.tbt_ms) === 'ok' ? 'good' : tbtStatus(scores.tbt_ms) === 'warn' ? 'needs work' : 'poor'}
                    </StatusPill>
                  )}
                </div>
                <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">
                  {scores.tbt_ms != null ? `${scores.tbt_ms}ms` : '—'}
                </div>
                <div className="text-[11px] text-cream/45 mt-1">target: &lt;200ms</div>
                {scores.tbt_ms != null && (
                  <div className="mt-3">
                    <BarTrack value={scores.tbt_ms} max={1000} tone={tbtStatus(scores.tbt_ms)} />
                  </div>
                )}
              </Card>
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-cream/40">CLS</span>
                  {scores.cls != null && (
                    <StatusPill tone={clsStatus(scores.cls)}>
                      {clsStatus(scores.cls) === 'ok' ? 'good' : clsStatus(scores.cls) === 'warn' ? 'needs work' : 'poor'}
                    </StatusPill>
                  )}
                </div>
                <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">
                  {scores.cls != null ? scores.cls.toFixed(3) : '—'}
                </div>
                <div className="text-[11px] text-cream/45 mt-1">target: &lt;0.1</div>
                {scores.cls != null && (
                  <div className="mt-3">
                    <BarTrack value={scores.cls} max={0.5} tone={clsStatus(scores.cls)} />
                  </div>
                )}
              </Card>
            </div>
          </section>

          {findings.length > 0 && (
            <section>
              <SectionLabel>Findings ({findings.length})</SectionLabel>
              <div className="space-y-2">
                {[...criticals, ...warnings].slice(0, 20).map((f) => (
                  <Card key={f.id}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-cream/85 text-sm">{f.message}</div>
                        <div className="text-[11px] text-cream/40 mt-0.5 truncate">{f.rule}</div>
                      </div>
                      <StatusPill tone={f.severity === 'critical' ? 'bad' : 'warn'}>
                        {f.severity}
                      </StatusPill>
                    </div>
                  </Card>
                ))}
                {findings.length === 0 && (
                  <Card>
                    <div className="text-cream/55 text-sm">No findings — looking clean.</div>
                  </Card>
                )}
              </div>
            </section>
          )}

          {findings.length === 0 && (
            <section>
              <SectionLabel>Findings</SectionLabel>
              <Card>
                <div className="text-cream/55 text-sm">No findings — looking clean.</div>
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}
